// Import model
import { ChildrenFieldOptions } from '../models/ChildrenFieldOptions';

// Import base class serializer
import { FieldOptionsSerializer } from './FieldOptionsSerializer';

// Import dependency types (models and enums used in properties)
import { Condition } from '../models/Condition';
import { Field } from '../models/Field';
import { Form } from '../models/Form';

// Import dependency serializers (for complex types)
import { ConditionSerializer } from './ConditionSerializer';
import { FieldSerializer } from './FieldSerializer';
import { FormSerializer } from './FormSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for ChildrenFieldOptions - handles JSON serialization and deserialization with type validation
 */
export class ChildrenFieldOptionsSerializer {
  /**
   * Deserializes a JSON string into a ChildrenFieldOptions instance
   */
  static deserialize(json: string): ChildrenFieldOptions {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a ChildrenFieldOptions instance
   */
  static fromObject(obj: any): ChildrenFieldOptions {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to ChildrenFieldOptions');
    }


    const instance: ChildrenFieldOptions = {
      t: obj['_t'] as string,
      linkUrl: obj['LinkUrl'] as string | undefined,
      linkLabel: obj['LinkLabel'] as string | undefined,
      url: obj['Url'] as string | undefined,
      objectType: obj['ObjectType'] as string | undefined,
      objectTypeName: obj['ObjectType|Name'] as string | undefined,
      keyType: obj['KeyType'] as string | undefined,
      criteria: obj['Criteria'] ? (obj['Criteria'] as any[]).map((item: any) =>
        ConditionSerializer.fromObject(item)
      ) : [] as any,
      keyField: obj['KeyField'] ? FieldSerializer.fromObject(obj['KeyField']) : undefined as any,
      addFormUrls: obj['AddFormUrls'] as any | undefined,
      editForms: obj['EditForms'] ? FormSerializer.fromObject(obj['EditForms']) : undefined as any,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of ChildrenFieldOptions instances
   */
  static deserializeArray(json: string): ChildrenFieldOptions[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a ChildrenFieldOptions instance to a JSON string
   */
  static serialize(instance: ChildrenFieldOptions): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a ChildrenFieldOptions instance to a plain object
   */
  static toObject(instance: ChildrenFieldOptions): any {
    return {
      '_t': instance.t,
      'LinkUrl': instance.linkUrl,
      'LinkLabel': instance.linkLabel,
      'Url': instance.url,
      'ObjectType': instance.objectType,
      'ObjectType|Name': instance.objectTypeName,
      'KeyType': instance.keyType,
      'Criteria': instance.criteria?.map((item: any) =>
        ConditionSerializer.toObject(item)
      ),
      'KeyField': instance.keyField ? FieldSerializer.toObject(instance.keyField) : undefined,
      'AddFormUrls': instance.addFormUrls,
      'EditForms': instance.editForms ? FormSerializer.toObject(instance.editForms) : undefined,
    };
  }

  /**
   * Serializes an array of ChildrenFieldOptions instances to a JSON string
   */
  static serializeArray(instances: ChildrenFieldOptions[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
